import '../../styles/restScreens.css';
import React, { useState } from 'react';
import { useSupportSession } from '../../context/SupportSessionContext';

function SupportAnnotationOverlay() {
    const {
        isSupportMode,
        annotations,
        isAnnotationMode,
        toggleAnnotationMode,
        addAnnotation,
        removeAnnotation
    } = useSupportSession();

    const [pendingCoord, setPendingCoord] = useState(null);
    const [noteText, setNoteText] = useState('');

    if (!isSupportMode) {
        return null;
    }

    const handleBackdropClick = (e) => {
        if (!isAnnotationMode) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const xPercent = ((e.clientX - rect.left) / rect.width) * 100;
        const yPercent = ((e.clientY - rect.top) / rect.height) * 100;

        setPendingCoord({ xPercent, yPercent, clientX: e.clientX, clientY: e.clientY });
        setNoteText('');
    };

    const handleSavePending = (e) => {
        e.preventDefault();
        if (pendingCoord) {
            addAnnotation({
                xPercent: pendingCoord.xPercent,
                yPercent: pendingCoord.yPercent,
                text: noteText.trim() || 'Highlighted Element',
                color: '#f97316'
            });
            setPendingCoord(null);
            setNoteText('');
            toggleAnnotationMode(false);
        }
    };

    const handleCancelPending = () => {
        setPendingCoord(null);
        setNoteText('');
    };

    return (
        <div data-m6-layout="support" data-m6-component="SupportAnnotationOverlay"
            className={`fixed inset-0 pointer-events-none z-50 ${
                isAnnotationMode ? 'cursor-crosshair' : ''
            }`}
        >
            {/* Click-capture layer when annotation mode is enabled */}
            {isAnnotationMode && (
                <div
                    onClick={handleBackdropClick}
                    className="absolute inset-0 pointer-events-auto bg-black/5 cursor-crosshair"
                    title="Click anywhere to place a support pointer or annotation note"
                />
            )}

            {/* Render active annotations */}
            {annotations.map((ann) => (
                <div
                    key={ann.id}
                    style={{
                        left: `${ann.xPercent}%`,
                        top: `${ann.yPercent}%`
                    }}
                    className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-auto group"
                >
                    {/* Pulsing ring */}
                    <div className="relative flex items-center justify-center">
                        <span
                            className="absolute inline-flex h-8 w-8 animate-ping rounded-full opacity-75"
                            style={{ backgroundColor: ann.color || '#f97316' }}
                        />
                        <div
                            className="relative flex items-center justify-center h-6 w-6 rounded-full text-white text-xs font-bold shadow-lg border-2 border-white cursor-pointer"
                            style={{ backgroundColor: ann.color || '#f97316' }}
                        >
                            📍
                        </div>
                    </div>

                    {/* Annotation Note Bubble */}
                    <div className="absolute left-1/2 top-7 -translate-x-1/2 min-w-[160px] max-w-xs p-2.5 rounded-lg shadow-xl bg-gray-900 text-white text-xs border border-gray-700 z-50 pointer-events-auto transition-all">
                        <div className="flex items-center justify-between gap-2 border-b border-gray-700 pb-1 mb-1.5">
                            <span className="font-semibold text-amber-400 text-[11px] truncate">
                                {ann.author}
                            </span>
                            <button
                                type="button"
                                onClick={() => removeAnnotation(ann.id)}
                                className="text-gray-400 hover:text-white font-bold text-xs"
                                title="Dismiss annotation"
                            >
                                ✕
                            </button>
                        </div>
                        <p className="text-gray-100 text-xs whitespace-pre-wrap leading-tight">{ann.text}</p>
                        <span className="text-[10px] text-gray-400 mt-1 block">
                            {new Date(ann.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                    </div>
                </div>
            ))}

            {/* New Annotation Form Modal / Popover */}
            {pendingCoord && (
                <div
                    style={{
                        left: `${pendingCoord.xPercent}%`,
                        top: `${pendingCoord.yPercent}%`
                    }}
                    className="absolute -translate-x-1/2 pointer-events-auto bg-gray-900 border border-amber-500 rounded-lg shadow-2xl p-3 min-w-[240px] z-50"
                >
                    <form onSubmit={handleSavePending}>
                        <div className="text-xs font-semibold text-amber-400 mb-1.5 flex items-center gap-1">
                            <span>📍</span>
                            <span>Add Support Annotation Note</span>
                        </div>
                        <textarea
                            value={noteText}
                            onChange={(e) => setNoteText(e.target.value)}
                            placeholder="e.g. Check account settings here..."
                            rows={2}
                            autoFocus
                            className="w-full text-xs bg-gray-800 text-white border border-gray-700 rounded p-1.5 outline-none focus:border-amber-500"
                        />
                        <div className="flex justify-end gap-1.5 mt-2">
                            <button
                                type="button"
                                onClick={handleCancelPending}
                                className="px-2 py-1 text-xs text-gray-300 hover:text-white"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-3 py-1 text-xs font-semibold bg-amber-600 hover:bg-amber-500 text-white rounded shadow"
                            >
                                Drop Pin
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}

export default SupportAnnotationOverlay;
